import logo from "../assets/logo.png";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  CalendarDaysIcon,
  MapPinIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { EVENT_DATA } from "../constants/eventData";
import { attendanceService } from "../services/attendanceService";
import { soundService } from "../services/soundService";
  
const HomePage: React.FC = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, 50]);
  const y2 = useTransform(scrollY, [0, 300], [0, -50]);

  // Use hardcoded event data instead of API call
  const event = EVENT_DATA;

  const [currentTime, setCurrentTime] = useState(new Date());
  const [showAttendanceFlash, setShowAttendanceFlash] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Check for entrance mode and auto-ping attendance
  useEffect(() => {
    const entranceMode = localStorage.getItem("entranceMode") === "true";

    if (entranceMode) {
      // Auto-fire attendance ping
      const pingAttendance = async () => {
        try {
          await attendanceService.pingAttendance();

          // Play sound
          soundService.playNotificationSound();

          // Show flash
          setShowAttendanceFlash(true);
          setTimeout(() => setShowAttendanceFlash(false), 2000);
        } catch (error) {
          console.error("Auto attendance ping failed:", error);
        }
      };

      pingAttendance();
    }
  }, []);

  // No loading or error handling needed since we're using hardcoded data

  const eventDate = new Date(event.date);
  const formattedDate = eventDate.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const timeUntilEvent = eventDate.getTime() - currentTime.getTime();
  const isEventPassed = timeUntilEvent <= 0;

  const daysUntil = isEventPassed ? 0 : Math.floor(timeUntilEvent / (1000 * 60 * 60 * 24));
  const hoursUntil = isEventPassed ? 0 : Math.floor((timeUntilEvent % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutesUntil = isEventPassed ? 0 : Math.floor((timeUntilEvent % (1000 * 60 * 60)) / (1000 * 60));
  const secondsUntil = isEventPassed ? 0 : Math.floor((timeUntilEvent % (1000 * 60)) / 1000);

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Attendance Flash */}
      {showAttendanceFlash && (
        <motion.div
          className="fixed inset-0 bg-green-500 bg-opacity-90 flex items-center justify-center z-50"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.3 }}
        >
          <div className="text-center text-white">
            <CheckCircleIcon className="w-24 h-24 mx-auto mb-4" />
            <h2 className="text-4xl font-bold">Counted +1</h2>
            <p className="text-xl">Welcome to the event!</p>
          </div>
        </motion.div>
      )}

      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800" />
        <motion.div className="absolute inset-0 opacity-20" style={{ y: y1 }}>
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse" />
          <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse" />
        </motion.div>
        <motion.div className="absolute inset-0 opacity-10" style={{ y: y2 }}>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse" />
        </motion.div>
      </div>

      {/* Navigation */}
      <motion.nav
        className="relative z-50 bg-primary-600/10 backdrop-blur-md border-b border-white/10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <motion.a
              href="/"
              className="flex items-center"
              whileHover={{ scale: 1.05 }}
            >
              <img
                src={logo}
                alt="Visa Logo"
                className="w-24 h-24 object-contain"
              />
            </motion.a>
            <Link
              to="/rsvp"
              className="group relative inline-flex items-center justify-center px-6 py-3 font-bold rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95 bg-white text-primary-600 hover:bg-primary-50"
            >
              <span className="relative z-10 font-visa font-bold">
                RSVP Now
              </span>
            </Link>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative z-10 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {/* Main Title */}
              <motion.h1
                className="text-5xl lg:text-7xl font-bold leading-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  {event.title}
                </span>
              </motion.h1>

              {/* Event Motto */}
              <motion.div
                className="text-xl lg:text-2xl font-semibold text-accent-300 tracking-wide"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                ACCELERATING ETHIOPIA'S DIGITAL FUTURE
              </motion.div>

              {/* Description */}
              <motion.div
                className="text-xl text-gray-300 leading-relaxed max-w-2xl space-y-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <p>
                  You are cordially invited to connect and engage at Visa
                  Connect Ethiopia 2025.
                </p>
              </motion.div>

              {/* Event Details */}
              <motion.div
                className="space-y-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.0 }}
              >
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <CalendarDaysIcon className="w-8 h-8 text-accent-400 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-white mb-1">
                        Date & Time
                      </h3>
                      <p className="text-white text-sm font-medium">
                        {formattedDate}
                      </p>
                      <p className="text-white text-sm">{event.time}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <MapPinIcon className="w-8 h-8 text-accent-400 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-white mb-1">
                        Location
                      </h3>
                      <p className="text-white text-sm font-medium">
                        {event.location}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                className="flex flex-col sm:flex-row gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.2 }}
              >
                <Link
                  to="/rsvp"
                  className="group relative inline-flex items-center justify-center px-10 py-5 font-bold rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95 bg-white text-primary-600 hover:bg-primary-50 overflow-hidden"
                >
                  <span className="relative z-10 font-visa font-bold text-xl">
                    RSVP Now
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-50 to-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>

              </motion.div>
            </motion.div>

            {/* Right Content - Event Image */}
            <motion.div
              className="relative hidden lg:block -mt-8"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="relative">
                {/* Main Image */}
                <motion.div
                  className="relative rounded-3xl overflow-hidden shadow-2xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  {event.imageUrl ? (
                    <img
                      src={event.imageUrl}
                      alt={event.title}
                      className="w-full h-[600px] object-cover"
                    />
                  ) : (
                    <div className="w-full h-[600px] bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center">
                      <CalendarDaysIcon className="w-32 h-32 text-white/50" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Agenda Section */}
      <motion.section
        className="relative z-10 py-20 bg-white/5 backdrop-blur-sm border-y border-white/10"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-4">Event Agenda</h2>
            <p className="text-gray-300 text-lg">
              Explore the future of payments in Ethiopia
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {/* Morning Sessions */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-white mb-6 text-center lg:text-left">
                Morning Sessions
              </h3>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    8:00 AM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Morning Coffee & Registration
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Welcome reception and networking
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    9:00 AM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Welcome & Agenda Overview
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Introduction to the day's program and objectives
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    9:10 AM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Enabling Ethiopia's Digital Economy
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Exploring Visa's priorities for expanding Ethiopia's
                      digital economy and building a strong payments ecosystem
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    9:40 AM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Vision for Ethiopia's Financial Future
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Central bank's strategy for digital transformation and
                      financial inclusion
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    10:00 AM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      CEO Panel: Driving Merchant Acceptance
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Exploring how expanding merchant acceptance can fuel
                      economic development and financial inclusion
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    11:00 AM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Data-Driven Growth
                    </h4>
                    <p className="text-gray-300 text-sm">
                      How data insights can help Ethiopian banks and fintechs
                      unlock their full potential
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    11:40 AM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Transforming Everyday Commerce
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Exploring consumer payment innovations and contactless
                      technologies
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Afternoon Sessions */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-white mb-6 text-center lg:text-left">
                Afternoon Sessions
              </h3>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    12:20 PM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Strategic Partnerships Panel
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Collaborating with fintechs and telcos to scale digital
                      payments and financial inclusion
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    12:50 PM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Cross-Border Payments & Remittances
                    </h4>
                    <p className="text-gray-300 text-sm">
                      How modern payment rails are transforming global money
                      movement
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    1:30 PM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Networking Lunch
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Connect with industry leaders and peers
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    2:30 PM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Building Stronger Brands
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Unlocking consumer loyalty with marketing value-added
                      services
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    3:10 PM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Audience Q&A
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Interactive session for questions and discussions
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    3:30 PM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Closing Remarks
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Summary and next steps
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                    3:50 PM
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Networking Session
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Extended networking and partnership opportunities
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Countdown Section */}
      <motion.section
        className="relative z-10 py-20 bg-white/5 backdrop-blur-sm border-y border-white/10"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              Event Countdown
            </h2>
            <p className="text-gray-300 text-lg">
              Don't miss this exclusive opportunity
            </p>
          </motion.div>

          <motion.div
            className="flex flex-row md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-12 max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="text-center flex-1 md:flex-none">
              <p className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-2 md:mb-3 font-visa">
                {daysUntil}
              </p>
              <p className="text-gray-300 text-sm md:text-base font-medium">
                Days
              </p>
            </div>
            <div className="text-center flex-1 md:flex-none">
              <p className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-2 md:mb-3 font-visa">
                {hoursUntil}
              </p>
              <p className="text-gray-300 text-sm md:text-base font-medium">
                Hours
              </p>
            </div>
            <div className="text-center flex-1 md:flex-none">
              <p className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-2 md:mb-3 font-visa">
                {minutesUntil}
              </p>
              <p className="text-gray-300 text-sm md:text-base font-medium">
                Minutes
              </p>
            </div>
            <div className="text-center flex-1 md:flex-none">
              <p className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-2 md:mb-3 font-visa">
                {secondsUntil}
              </p>
              <p className="text-gray-300 text-sm md:text-base font-medium">
                Seconds
              </p>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Footer */}
      <motion.footer
        className="relative z-10 bg-primary-800/30 backdrop-blur-sm border-t border-white/10 py-6"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <span className="text-xl font-bold text-white">
                VISA CONNECT ETHIOPIA 2025
              </span>
            </div>
            <p className="text-gray-400 mb-4">© 2025 All rights reserved.</p>
          </div>
        </div>
      </motion.footer>
    </div>
  );
};

export default HomePage;
